// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import type { Seat } from '../data.ts';
import { GROUP_META, GROUP_ORDER } from '../parties.ts';
import { STATE_NAMES } from '../data.ts';
import { fmtPct, fmtSigned, esc, fullName } from '../utils/format.ts';

type SortKey = 'name' | 'state' | 'member' | 'party' | 'margin' | 'tpp' | 'swing' | 'turnout' | 'informal';

const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export function renderExplorer(root: HTMLElement, ctx: Ctx): void {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  let sortKey: SortKey = (params.get('sort') as SortKey) || 'name';
  let asc = params.get('dir') !== 'desc';
  let q = '';
  let stateFilter = '';
  let groupFilter = '';

  root.innerHTML = `
    <div class="view-head">
      <h2>Every seat</h2>
      <p class="subtitle">All 150 electorates — search, filter and sort. Click a row for the full result.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <input class="text-input" id="ex-search" type="search" placeholder="Search seat or member…" style="min-width:200px" aria-label="Search" />
        <select id="ex-state" aria-label="Filter by state"><option value="">All states</option>${STATES.map((s) => `<option value="${s}">${STATE_NAMES[s]}</option>`).join('')}</select>
        <select id="ex-group" aria-label="Filter by party"><option value="">All parties</option>${GROUP_ORDER.map((g) => `<option value="${g}">${GROUP_META[g].label}</option>`).join('')}</select>
        <span id="ex-count" class="note" style="margin:0 0 0 auto"></span>
      </div>
      <div class="table-scroll">
        <table class="data" id="ex-table">
          <thead><tr>
            <th data-k="name">Seat</th>
            <th data-k="state">State</th>
            <th data-k="member">Member</th>
            <th data-k="party">Party</th>
            <th class="num" data-k="margin">Margin</th>
            <th class="num" data-k="tpp">ALP 2PP</th>
            <th class="num" data-k="swing">Swing</th>
            <th class="num" data-k="turnout">Turnout</th>
            <th class="num" data-k="informal">Informal</th>
          </tr></thead>
          <tbody id="ex-body"></tbody>
        </table>
      </div>
    </div>`;

  const body = root.querySelector('#ex-body')!;
  const countEl = root.querySelector('#ex-count')!;

  const val = (s: Seat, k: SortKey): number | string => {
    switch (k) {
      case 'name': return s.name;
      case 'state': return s.state;
      case 'member': return s.member.surname;
      case 'party': return s.member.group;
      case 'margin': return s.marginPct;
      case 'tpp': return s.tppALPPct;
      case 'swing': return s.tppSwingToALP ?? (asc ? 9999 : -9999); // nulls sort to the end either way
      case 'turnout': return s.turnoutPct;
      case 'informal': return s.informalPct;
    }
  };

  const draw = () => {
    let rows = ctx.data.seats.filter((s) => {
      if (stateFilter && s.state !== stateFilter) return false;
      if (groupFilter && s.member.group !== groupFilter) return false;
      if (q) {
        const hay = `${s.name} ${fullName(s.member.given, s.member.surname)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      const va = val(a, sortKey), vb = val(b, sortKey);
      const c = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return asc ? c : -c;
    });
    countEl.textContent = `${rows.length} of 150 seats`;
    body.innerHTML = rows.map((s) => `
      <tr data-seat="${esc(s.name)}">
        <td><strong>${esc(s.name)}</strong></td>
        <td>${esc(s.state)}</td>
        <td>${esc(fullName(s.member.given, s.member.surname))}</td>
        <td><span class="pill" style="background:${GROUP_META[s.member.group].color}"><span class="dot"></span>${esc(s.member.party)}</span></td>
        <td class="num">${s.marginPct.toFixed(s.marginPct < 0.1 ? 2 : 1)}</td>
        <td class="num">${fmtPct(s.tppALPPct)}</td>
        <td class="num" style="color:${s.tppSwingToALP == null ? 'var(--text-tertiary)' : s.tppSwingToALP >= 0 ? 'var(--alp)' : 'var(--coa)'}">${s.tppSwingToALP == null ? '—' : fmtSigned(s.tppSwingToALP)}</td>
        <td class="num">${fmtPct(s.turnoutPct)}</td>
        <td class="num">${fmtPct(s.informalPct)}</td>
      </tr>`).join('');
    body.querySelectorAll<HTMLElement>('tr[data-seat]').forEach((tr) => {
      tr.addEventListener('click', () => ctx.openSeat(tr.dataset.seat!));
    });
    root.querySelectorAll<HTMLElement>('th[data-k]').forEach((th) => {
      const k = th.dataset.k as SortKey;
      const base = th.textContent!.replace(/[▲▼]\s*$/, '').trim();
      th.innerHTML = k === sortKey ? `${base} <span class="caret">${asc ? '▲' : '▼'}</span>` : base;
    });
  };

  root.querySelectorAll<HTMLElement>('th[data-k]').forEach((th) => {
    th.addEventListener('click', () => {
      const k = th.dataset.k as SortKey;
      if (k === sortKey) asc = !asc;
      else { sortKey = k; asc = k === 'name' || k === 'state' || k === 'member' || k === 'party'; }
      draw();
    });
  });
  const search = root.querySelector('#ex-search') as HTMLInputElement;
  let t: number | undefined;
  search.addEventListener('input', () => { window.clearTimeout(t); t = window.setTimeout(() => { q = search.value.trim().toLowerCase(); draw(); }, 250); });
  (root.querySelector('#ex-state') as HTMLSelectElement).addEventListener('change', (e) => { stateFilter = (e.target as HTMLSelectElement).value; draw(); });
  (root.querySelector('#ex-group') as HTMLSelectElement).addEventListener('change', (e) => { groupFilter = (e.target as HTMLSelectElement).value; draw(); });

  draw();
}
